// components/organism/Login/LoginCredentialForm/LoginCredentialForm.tsx
import React, { type FormEvent, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { z } from 'zod';
import { Button, Input, Label } from 'shared-portal-components-v2';

const FIRST_ERROR_INDEX = 0;

const loginSchema = z.object({
  username: z.string().email('Please enter a valid email').nonempty('Username is required'),
  password: z.string().nonempty('Password is required'),
});

interface LoginFieldErrors {
  username?: string;
  password?: string;
}

export interface LoginCredentialsFormProps {
  onSubmit: (values: { username: string; password: string }) => void | Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  fieldErrors?: LoginFieldErrors;
}

export default function LoginCredentialsForm({
  onSubmit,
  isLoading = false,
  error,
  fieldErrors,
}: LoginCredentialsFormProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const parsed = useMemo(
    () => loginSchema.safeParse({ username: username.trim(), password }),
    [username, password],
  );

  const usernameValidationError = useMemo(() => {
    if (!hasSubmitted || parsed.success) return undefined;
    return parsed.error.flatten().fieldErrors.username?.[FIRST_ERROR_INDEX];
  }, [hasSubmitted, parsed]);

  const passwordValidationError = useMemo(() => {
    if (!hasSubmitted || parsed.success) return undefined;
    return parsed.error.flatten().fieldErrors.password?.[FIRST_ERROR_INDEX];
  }, [hasSubmitted, parsed]);

  const isContinueDisabled = isLoading || (hasSubmitted && !parsed.success);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setHasSubmitted(true);
    if (!parsed.success) return;
    void onSubmit({ username: parsed.data.username, password: parsed.data.password });
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.target.value);
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setUsername(event.target.value);
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      {error !== undefined && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="login-username" isRequired className="text-base">
            Username
          </Label>
          <Input
            id="login-username"
            type="text"
            required
            placeholder="Enter your email"
            value={username}
            onChange={handleUsernameChange}
            variant="outline"
            inputClassName="h-12 text-base sm:text-sm"
            error={usernameValidationError ?? fieldErrors?.username}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="login-password" isRequired className="text-base">
            Password
          </Label>

          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              required
              placeholder="Enter your password"
              value={password}
              onChange={handlePasswordChange}
              variant="outline"
              inputClassName="h-12 text-base sm:text-sm pr-12"
              error={passwordValidationError ?? fieldErrors?.password}
            />

            <Button
              type="button"
              variant="tertiary"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground bg-transparent shadow-none"
              onClick={() => {
                setShowPassword((prev) => !prev);
              }}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
              <span className="sr-only">
                {showPassword ? 'Hide password' : 'Show password'}
              </span>
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <Link
            to="/forgot-password"
            className="text-sm text-accent hover:text-accent/80"
          >
            Forgot password?
          </Link>
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full h-12"
        isLoading={isLoading}
        loadingText="Signing in..."
        disabled={isContinueDisabled}
      >
        Continue
      </Button>
    </form>
  );
}
